import React, { useMemo, useImperativeHandle, forwardRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface Moodboard {
  id: number;
  name: string;
  user_id: number;
  user_email?: string;
  user_name?: string;
  is_public: boolean;
  likes_count: number;
  shares_count?: number;
  saves_count?: number;
  views_count: number;
  location?: string;
  created_at: string;
  is_hidden?: boolean;
  images?: string[];
  moderation_status?: string;
  reported_count?: number;
  reported_at?: string;
}

interface MoodboardsGridProps {
  moodboards: Moodboard[];
  searchValue?: string;
  onHideMoodboard?: (moodboardId: number) => void;
  onShowMoodboard?: (moodboardId: number) => void;
  onRejectMoodboard?: (moodboardId: number) => void;
  onApproveMoodboard?: (moodboardId: number) => void;
  onDeleteMoodboard?: (moodboardId: number) => void;
  onFlagMoodboard?: (moodboardId: number) => void;
}

const MoodboardsGrid = forwardRef<any, MoodboardsGridProps>(
  (
    {
      moodboards,
      searchValue = '',
      onHideMoodboard,
      onShowMoodboard,
      onRejectMoodboard,
      onApproveMoodboard,
      onDeleteMoodboard,
      onFlagMoodboard,
    },
    ref
  ) => {
    const { colors } = useTheme();
    const gridRef = React.useRef<AgGridReact>(null);
    const gridApiRef = React.useRef<any>(null);

    // Only render on web
    if (Platform.OS !== 'web') {
      return null;
    }

    // Filter moodboards based on search value
    const filteredMoodboards = React.useMemo(() => {
      if (!searchValue.trim()) return moodboards;
      const lowerSearch = searchValue.toLowerCase();
      return moodboards.filter((mb) => {
        const searchFields = [
          mb.name,
          mb.user_id?.toString(),
          mb.user_email,
          mb.user_name,
          mb.id?.toString(),
        ];
        return searchFields.some(
          (field) => field && String(field).toLowerCase().includes(lowerSearch)
        );
      });
    }, [moodboards, searchValue]);

    const columnDefs: ColDef[] = useMemo(
      () => [
        { field: 'id', headerName: 'ID', width: 80, sortable: true },
        {
          field: 'images',
          headerName: 'Images',
          width: 300,
          sortable: false,
          valueFormatter: () => '', // Suppress warning for object type
          cellRenderer: (params: any) => {
            let images = params.value || [];
            const borderColor = colors.border;
            const _secondaryColor = colors.secondary;

            // Ensure images is an array
            if (!Array.isArray(images)) {
              // Try to parse if it's a JSON string
              if (typeof images === 'string') {
                try {
                  images = JSON.parse(images);
                } catch {
                  images = [];
                }
              } else {
                images = [];
              }
            }

            if (images.length === 0) {
              return (
                <span style={{ color: palette.mediumGray3, fontSize: '11px' }}>
                  No images
                </span>
              );
            }
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: '100%',
                  flexWrap: 'wrap',
                  padding: '4px 0',
                }}
              >
                {images.map((imageUrl: string, index: number) => (
                  <img
                    key={index}
                    src={imageUrl}
                    alt={`Moodboard ${index + 1}`}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      border: `1px solid ${borderColor}`,
                      flexShrink: 0,
                    }}
                    onError={(e: any) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
            );
          },
        },
        {
          field: 'name',
          headerName: 'Name',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'user_id',
          headerName: 'User',
          width: 200,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const user = params.data;
            const _userName =
              user.user_name || user.user_email || `ID: ${user.user_id}`;
            return (
              <div style={{ fontSize: '12px' }}>
                {user.user_name && (
                  <div style={{ fontWeight: '500' }}>{user.user_name}</div>
                )}
                <div style={{ color: palette.darkGrayBlue, fontSize: '11px' }}>
                  ID: {user.user_id}
                </div>
                {user.user_email && (
                  <div
                    style={{ color: palette.darkGrayBlue, fontSize: '11px' }}
                  >
                    {user.user_email}
                  </div>
                )}
              </div>
            );
          },
        },
        {
          field: 'is_public',
          headerName: 'Public',
          width: 100,
          sortable: true,
          cellRenderer: (params: any) => {
            return params.value ? 'Yes' : 'No';
          },
        },
        {
          field: 'location',
          headerName: 'Location',
          width: 150,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const location = params.value;
            if (!location) {
              return (
                <span style={{ color: palette.mediumGray3, fontSize: '11px' }}>
                  No location
                </span>
              );
            }
            return (
              <span
                style={{
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span style={{ fontSize: '12px' }}>📍</span>
                <span>{location}</span>
              </span>
            );
          },
        },
        {
          field: 'likes_count',
          headerName: 'Likes',
          width: 100,
          sortable: true,
          cellRenderer: (params: any) => {
            const count = params.value || 0;
            return (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: palette.brightMatteRed,
                }}
              >
                <span style={{ fontSize: '12px' }}>❤️</span>
                <span>{count}</span>
              </span>
            );
          },
        },
        {
          field: 'saves_count',
          headerName: 'Saved',
          width: 100,
          sortable: true,
          cellRenderer: (params: any) => {
            const count = params.value || 0;
            return (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: palette.cyanLimeGreen,
                }}
              >
                <span style={{ fontSize: '12px' }}>🔖</span>
                <span>{count}</span>
              </span>
            );
          },
        },
        {
          field: 'shares_count',
          headerName: 'Share',
          width: 100,
          sortable: true,
          cellRenderer: (params: any) => {
            const count = params.value || 0;
            return (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: palette.brightBlue,
                }}
              >
                <span style={{ fontSize: '12px' }}>📤</span>
                <span>{count}</span>
              </span>
            );
          },
        },
        {
          field: 'views_count',
          headerName: 'Views',
          width: 100,
          sortable: true,
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 180,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          field: 'moderation_status',
          headerName: 'Moderation Status',
          width: 140,
          sortable: true,
          cellRenderer: (params: any) => {
            const status = params.value || 'active';
            // Map 'pending' to 'active' since moodboards don't need approval
            const displayStatus = status === 'pending' ? 'active' : status;
            const statusColors: { [key: string]: string } = {
              active: palette.cyanLimeGreen,
              approved: palette.cyanLimeGreen,
              rejected: palette.brightMatteRed,
              flagged: palette.lightOrange,
            };
            const color = statusColors[displayStatus] || palette.darkGrayBlue;
            return (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: `${color}20`,
                  color: color,
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'capitalize',
                }}
              >
                {displayStatus}
              </span>
            );
          },
        },
        {
          field: 'reported_count',
          headerName: 'Reports',
          width: 90,
          sortable: true,
          cellRenderer: (params: any) => {
            const count = params.value || 0;
            if (count === 0)
              return (
                <span style={{ color: palette.mediumGray3, fontSize: '11px' }}>
                  -
                </span>
              );
            return (
              <span
                style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  backgroundColor: `${palette.lightOrange}20`,
                  color: palette.lightOrange,
                  fontSize: '11px',
                  fontWeight: '600',
                }}
              >
                {count}
              </span>
            );
          },
        },
        {
          headerName: 'Actions',
          width: 350,
          cellRenderer: (params: any) => {
            const moodboard = params.data;
            const isHidden = moodboard.is_hidden || false;
            const status = moodboard.moderation_status || 'active';
            return (
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  height: '100%',
                  flexWrap: 'wrap',
                }}
              >
                {status === 'flagged' && onRejectMoodboard && (
                  <button
                    onClick={() => onRejectMoodboard(moodboard.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.brightMatteRed,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Reject Flagged Request"
                  >
                    Reject
                  </button>
                )}
                {status === 'flagged' && onApproveMoodboard && (
                  <button
                    onClick={() => onApproveMoodboard(moodboard.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.cyanLimeGreen,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Approve (Hide) Flagged Content"
                  >
                    Approve
                  </button>
                )}
                {status === 'flagged' && onDeleteMoodboard && (
                  <button
                    onClick={() => onDeleteMoodboard(moodboard.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.brightRed,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Delete Flagged Content"
                  >
                    Delete
                  </button>
                )}
                {status !== 'flagged' &&
                  status !== 'rejected' &&
                  onRejectMoodboard && (
                    <button
                      onClick={() => onRejectMoodboard(moodboard.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: palette.brightMatteRed,
                        color: palette.white,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                      }}
                      title="Reject"
                    >
                      Reject
                    </button>
                  )}
                {status !== 'flagged' && onDeleteMoodboard && (
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          `Are you sure you want to delete moodboard "${moodboard.name}"? This action cannot be undone.`
                        )
                      ) {
                        onDeleteMoodboard(moodboard.id);
                      }
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.brightRed,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Delete"
                  >
                    Delete
                  </button>
                )}
                {status !== 'flagged' && onFlagMoodboard && (
                  <button
                    onClick={() => onFlagMoodboard(moodboard.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.lightOrange,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Flag"
                  >
                    Flag
                  </button>
                )}
                {status !== 'flagged' && isHidden && onShowMoodboard && (
                  <button
                    onClick={() => onShowMoodboard(moodboard.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.cyanLimeGreen,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Show"
                  >
                    Show
                  </button>
                )}
                {status !== 'flagged' && !isHidden && onHideMoodboard && (
                  <button
                    onClick={() => onHideMoodboard(moodboard.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.darkGrayBlue,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Hide"
                  >
                    Hide
                  </button>
                )}
              </div>
            );
          },
        },
      ],
      [
        onHideMoodboard,
        onShowMoodboard,
        onRejectMoodboard,
        onDeleteMoodboard,
        onFlagMoodboard,
        colors.border,
        colors.secondary,
      ]
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: 'legacy',
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
        },
        pagination: false,
        rowHeight: 100,
        headerHeight: 40,
        suppressCellFocus: true,
        animateRows: true,
        suppressPaginationPanel: true,
        onGridReady: (params) => {
          gridApiRef.current = params.api;
        },
      }),
      []
    );

    // No need for useEffect - filtering is done via filteredMoodboards

    useImperativeHandle(ref, () => ({
      getApi: () => gridRef.current?.api,
    }));

    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <div
          className="ag-theme-alpine"
          style={
            {
              height: 'calc(100vh - 200px)',
              width: '100%',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              '--ag-header-background-color': colors.card,
              '--ag-background-color': colors.background,
              '--ag-odd-row-background-color': colors.card,
              '--ag-row-hover-color': colors.card,
              '--ag-header-foreground-color': colors.text,
              '--ag-foreground-color': colors.text,
              '--ag-border-color': colors.border,
              '--ag-secondary-border-color': colors.border,
              '--ag-input-focus-border-color': colors.primary,
              '--ag-selected-row-background-color': `${colors.primary}20`,
              '--ag-range-selection-background-color': `${colors.primary}15`,
              '--ag-range-selection-border-color': colors.primary,
              '--ag-checkbox-checked-color': colors.primary,
              '--ag-checkbox-unchecked-color': colors.border,
              '--ag-input-disabled-background-color': colors.card,
              '--ag-input-disabled-border-color': colors.border,
              '--ag-input-disabled-foreground-color': colors.secondary,
              '--ag-panel-background-color': colors.card,
              '--ag-tool-panel-background-color': colors.card,
              '--ag-side-button-selected-background-color': colors.primary,
              '--ag-side-button-selected-foreground-color': palette.white,
            } as React.CSSProperties & { [key: string]: any }
          }
        >
          <style>{`
          .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
          .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
            display: none;
          }
          .ag-theme-alpine .ag-body-viewport,
          .ag-theme-alpine .ag-body-horizontal-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .ag-theme-alpine {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-size: 12px;
          }
          .ag-theme-alpine .ag-header {
            border-bottom: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell {
            border-right: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell-text {
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-row {
            border-bottom: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine .ag-cell {
            border-right: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
          }
          .ag-theme-alpine .ag-cell-value {
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine input,
          .ag-theme-alpine select,
          .ag-theme-alpine button {
            font-size: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
        `}</style>
          <AgGridReact
            ref={gridRef}
            rowData={filteredMoodboards}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            domLayout="normal"
          />
        </div>
      </View>
    );
  }
);

MoodboardsGrid.displayName = 'MoodboardsGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
});

export default MoodboardsGrid;
